import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import Select from "react-select";
import "../../styles/common/popup.scss";

const UploadMaterialPopup = ({
  open,
  handleClose,
  onSave,
  materialType = "",
  pageData,
  context,
  courseKey,
  subjectKey,
  topicKey,
  subTopicKey,
  pageType = "content",
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [materialUrl, setMaterialUrl] = useState("");
  const [selectedType, setSelectedType] = useState(materialType);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [materialName, setMaterialName] = useState("");
  const fileInputRef = useRef(null);

  const materialTypes = [
    { value: "pdf", label: "PDF" },
    { value: "ppt", label: "PPT" },
    { value: "video", label: "Video" },
    { value: "audio", label: "Audio" },
    { value: "image", label: "Image" },
    { value: "doc", label: "Document" },
    { value: "mindmap", label: "Mindmap" },
  ];

  const selectStyles = {
    control: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-bg)",
      borderColor: "var(--border-color)",
      color: "var(--color-text-dark)",
      minHeight: "40px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "var(--color-primary)",
      },
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: "var(--color-bg)",
      zIndex: 10001,
      position: "absolute",
      width: "100%",
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 10001,
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? "var(--color-primary)"
        : state.isFocused
          ? "var(--color-hover)"
          : "var(--color-bg)",
      color: state.isSelected ? "white" : "var(--color-text-dark)",
      "&:hover": {
        backgroundColor: "var(--color-hover)",
      },
      fontSize: "var(--fs-md)",
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "var(--color-text-dark)",
      fontSize: "var(--fs-md)",
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: "var(--fs-md)",
    }),
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setError("");

      const extension = file.name.split(".").pop().toLowerCase();
      let detectedType = "other";

      if (extension === "pdf") detectedType = "pdf";
      else if (["ppt", "pptx"].includes(extension)) detectedType = "ppt";
      else if (["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(extension))
        detectedType = "video";
      else if (["mp3", "wav", "ogg", "aac"].includes(extension))
        detectedType = "audio";
      else if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(extension))
        detectedType = "image";
      else if (["doc", "docx"].includes(extension)) detectedType = "doc";

      setSelectedType(detectedType);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      setSelectedFile(file);
      setError("");

      const extension = file.name.split(".").pop().toLowerCase();
      let detectedType = "other";

      if (extension === "pdf") detectedType = "pdf";
      else if (["ppt", "pptx"].includes(extension)) detectedType = "ppt";
      else if (["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(extension))
        detectedType = "video";
      else if (["mp3", "wav", "ogg", "aac"].includes(extension))
        detectedType = "audio";
      else if (["jpg", "jpeg", "png", "gif", "bmp", "svg"].includes(extension))
        detectedType = "image";
      else if (["doc", "docx"].includes(extension)) detectedType = "doc";

      setSelectedType(detectedType);
    }
  };

  const handleSave = async () => {
    if (!materialName.trim()) {
      setError("Please enter a material name");
      return;
    }

    if (selectedType === "pdf" && !selectedFile) {
      setError("Please select a PDF file for upload");
      return;
    }

    if (selectedType === "video" && !selectedFile && !materialUrl.trim()) {
      setError("Please select a video file or enter a video URL");
      return;
    }

    if (
      (selectedType === "audio" ||
        selectedType === "image" ||
        selectedType === "doc" ||
        selectedType === "mindmap") &&
      !selectedFile
    ) {
      setError(`Please select a ${selectedType} file for upload`);
      return;
    }

    if (
      (selectedType === "ppt" || selectedType === "other") &&
      !selectedFile &&
      !materialUrl.trim()
    ) {
      setError("Please select a file or enter a URL");
      return;
    }

    if (!selectedType) {
      setError("Please select a material type");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      let contentUrl = materialUrl.trim();
      let fileToUpload = selectedFile;

      if (selectedFile && selectedType !== "video") {
        contentUrl = selectedFile.name;
        fileToUpload = selectedFile;
      }

      const materialData = {
        name: materialName.trim(),
        title: "subtopic",
        key: (() => {
          if (subTopicKey) return subTopicKey;
          if (topicKey) return topicKey;
          if (subjectKey) return subjectKey;
          if (courseKey) return courseKey;
          return pageData?.key || "";
        })(),
        pageid: pageData?.id || "",
        type: selectedType,
        ...(materialUrl.trim()
          ? { url: materialUrl.trim() }
          : { file: fileToUpload || "material.pdf" }),
      };

      if (onSave) {
        await onSave(materialData);
      }

      handleClose();
    } catch (err) {
      setError("Failed to upload material. Please try again.");
      console.error("Error uploading material:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClosePopup = () => {
    setSelectedFile(null);
    setMaterialUrl("");
    setSelectedType(materialType);
    setMaterialName("");
    setError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    handleClose();
  };

  const isSaveDisabled =
    isLoading ||
    !materialName.trim() ||
    !selectedType ||
    (selectedType === "pdf" && !selectedFile) ||
    (selectedType === "video" && !selectedFile && !materialUrl.trim()) ||
    ((selectedType === "audio" ||
      selectedType === "image" ||
      selectedType === "doc" ||
      selectedType === "mindmap") &&
      !selectedFile) ||
    ((selectedType === "ppt" || selectedType === "other") &&
      !selectedFile &&
      !materialUrl.trim());

  return createPortal(
    <div
      className={`common-popup-overlay ${open ? "" : "hidden"} high-z-index`}
      onClick={handleClosePopup}
    >
      <div
        className="common-popup-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="popup-header">
          <h2>
            {`Upload ${selectedType ? materialTypes.find((t) => t.value === selectedType)?.label : "Materials"}`}
          </h2>
          <button className="popup-close-button" onClick={handleClosePopup}>
            ×
          </button>
        </div>

        <div className="popup-content">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
          >
            <div className="popup-form-group">
              <label htmlFor="material-name">Name</label>
              <input
                id="material-name"
                type="text"
                className="popup-input"
                placeholder="Enter material name"
                value={materialName}
                onChange={(e) => setMaterialName(e.target.value)}
              />
            </div>

            <div className="popup-form-group">
              <label>Upload File</label>
              <div
                className="file-drop-zone"
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: "2px dashed #ccc",
                  borderRadius: "8px",
                  padding: "40px",
                  textAlign: "center",
                  cursor: "pointer",
                  backgroundColor: "#f9f9f9",
                }}
              >
                <div style={{ marginBottom: "10px" }}>
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ margin: "0 auto", color: "#ccc" }}
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7,10 12,15 17,10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </div>
                <p style={{ margin: "0", color: "#666" }}>
                  {selectedFile
                    ? selectedFile.name
                    : `Drag & drop ${selectedType ? materialTypes.find((t) => t.value === selectedType)?.label?.toLowerCase() || "file" : "file"} or Browse`}
                </p>
                <p
                  style={{ margin: "5px 0 0", fontSize: "12px", color: "#999" }}
                >
                  Support for PDF, PPT, Video, Audio, Image, Document files
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept=".pdf,.ppt,.pptx,.mp4,.avi,.mov,.wmv,.flv,.webm,.mp3,.wav,.ogg,.aac,.jpg,.jpeg,.png,.gif,.bmp,.svg,.doc,.docx"
                />
              </div>
            </div>

            {(selectedType === "video" ||
              selectedType === "ppt" ||
              selectedType === "other") && (
              <div className="popup-form-group">
                <label htmlFor="material-url">Or Enter URL</label>
                <input
                  id="material-url"
                  type="url"
                  className="popup-input"
                  placeholder={
                    selectedType
                      ? `https://example.com/material.${selectedType === "ppt" ? "pptx" : selectedType}`
                      : "https://example.com/material.pdf"
                  }
                  value={materialUrl}
                  onChange={(e) => setMaterialUrl(e.target.value)}
                />
              </div>
            )}

            {error && (
              <div className="popup-form-group">
                <div style={{ color: "red", fontSize: "14px" }}>{error}</div>
              </div>
            )}

            <div className="popup-actions">
              <button
                type="button"
                className="popup-cancel-button"
                onClick={handleClosePopup}
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="popup-submit-button"
                disabled={isSaveDisabled}
              >
                {isLoading
                  ? "Uploading..."
                  : `Upload  ${selectedType ? materialTypes.find((t) => t.value === selectedType)?.label : "Materials"}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default UploadMaterialPopup;
